import { markRaw } from 'vue'
import CompanyRecruiterNameColumn from './CompanyRecruiterNameColumn.vue'
import CompanyRecruiterStatusColumn from './CompanyRecruiterStatusColumn.vue'
import CompanyRecruiterFeeColumn from './CompanyRecruiterFeeColumn.vue'
import CompanyRecruiterOwnershipColumn from './CompanyRecruiterOwnershipColumn.vue'
import CompanyRecruiterSubmissionLimitColumn from './CompanyRecruiterSubmissionLimitColumn.vue'
import CompanyRecruiterTypeColumn from './CompanyRecruiterTypeColumn.vue'
import CompanyRecruiterTagsColumn from './CompanyRecruiterTagsColumn.vue'
import CompanyRecruiterActionColumn from './CompanyRecruiterActionColumn.vue'
import CompanyRecruiterEndClientsColumn from './CompanyRecruiterEndClientsColumn.vue'
import {
  CompanyRecruiterNameColumnFragmentDoc,
  CompanyRecruiterNameColumnFragment,
  CompanyRecruiterStatusColumnFragmentDoc,
  CompanyRecruiterStatusColumnFragment,
  CompanyRecruiterFeeColumnFragmentDoc,
  CompanyRecruiterFeeColumnFragment,
  CompanyRecruiterOwnershipColumnFragmentDoc,
  CompanyRecruiterOwnershipColumnFragment,
  CompanyRecruiterSubmissionLimitColumnFragmentDoc,
  CompanyRecruiterSubmissionLimitColumnFragment,
  CompanyRecruiterTypeColumnFragmentDoc,
  CompanyRecruiterTagsColumnFragmentDoc,
  CompanyRecruiterTagsColumnFragment,
  CompanyRecruiterActionColumnFragmentDoc,
  CompanyRecruiterEndClientsColumnFragmentDoc,
  CompanyRecruiterEndClientsColumnFragment,
  FeeApplication,
  RecruiterOrderByColumn,
  SortOrder,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'

const columns: ColumnMaps = {
  CompanyRecruiterNameColumn: {
    fragment: CompanyRecruiterNameColumnFragmentDoc,
    component: markRaw(CompanyRecruiterNameColumn),
    columnDef: {
      title: $t('Staffing agency'),
      key: 'CompanyRecruiterNameColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(CompanyRecruiterNameColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Staffing agency name',
        extraction: (row: CompanyRecruiterNameColumnFragment) => row.recruiter.name,
      },
      {
        title: 'Email',
        extraction: (row: CompanyRecruiterNameColumnFragment) => row.email ?? '',
      },
    ],
    sort: [
      {
        name: `${RecruiterOrderByColumn.RecruiterName}_${SortOrder.Asc}`,
        orderByColumn: RecruiterOrderByColumn.RecruiterName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Staffing agency'),
        secondaryText: $t('A - Z'),
        title: $t('Order by staffing agency name'),
      },
      {
        name: `${RecruiterOrderByColumn.RecruiterName}_${SortOrder.Desc}`,
        orderByColumn: RecruiterOrderByColumn.RecruiterName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Staffing agency'),
        secondaryText: $t('Z - A'),
        title: $t('Order by staffing agency name'),
      },
    ],
  },
  CompanyRecruiterStatusColumn: {
    fragment: CompanyRecruiterStatusColumnFragmentDoc,
    component: markRaw(CompanyRecruiterStatusColumn),
    columnDef: {
      title: $t('Status'),
      key: 'CompanyRecruiterStatusColumn',
      orderAble: false,
      renderUsing: {
        component: markRaw(CompanyRecruiterStatusColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Status',
        extraction: (row: CompanyRecruiterStatusColumnFragment) => `${row.status}`,
      },
    ],
    sort: [
      {
        name: `${RecruiterOrderByColumn.RecruiterStatus}_${SortOrder.Asc}`,
        orderByColumn: RecruiterOrderByColumn.RecruiterStatus,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Status'),
        secondaryText: $t('A - Z'),
        title: $t('Order by status'),
      },
      {
        name: `${RecruiterOrderByColumn.RecruiterStatus}_${SortOrder.Desc}`,
        orderByColumn: RecruiterOrderByColumn.RecruiterStatus,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Status'),
        secondaryText: $t('Z - A'),
        title: $t('Order by status'),
      },
    ],
  },
  CompanyRecruiterFeeColumn: {
    fragment: CompanyRecruiterFeeColumnFragmentDoc,
    component: markRaw(CompanyRecruiterFeeColumn),
    columnDef: {
      title: $t('Fee'),
      key: 'CompanyRecruiterFeeColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(CompanyRecruiterFeeColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Fee',
        extraction: (row: CompanyRecruiterFeeColumnFragment) =>
          row.recruiterManagesWorkers
            ? '-'
            : (row.fees
                .filter((fee) => fee.application === FeeApplication.Additive)
                .map((fee) => fee.formattedRate)
                .join(' + ') || '-'),
      },
    ],
    sort: [
      {
        name: `${RecruiterOrderByColumn.RecruiterFee}_${SortOrder.Asc}`,
        orderByColumn: RecruiterOrderByColumn.RecruiterFee,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Fee'),
        secondaryText: $t('Low to high'),
        title: $t('Order by fee'),
      },
      {
        name: `${RecruiterOrderByColumn.RecruiterFee}_${SortOrder.Desc}`,
        orderByColumn: RecruiterOrderByColumn.RecruiterFee,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Fee'),
        secondaryText: $t('High to low'),
        title: $t('Order by fee'),
      },
    ],
  },
  CompanyRecruiterOwnershipColumn: {
    fragment: CompanyRecruiterOwnershipColumnFragmentDoc,
    component: markRaw(CompanyRecruiterOwnershipColumn),
    columnDef: {
      title: $t('Ownership days'),
      key: 'CompanyRecruiterOwnershipColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(CompanyRecruiterOwnershipColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Ownership days',
        extraction: (row: CompanyRecruiterOwnershipColumnFragment) =>
          `${row.recruiterOwnershipDays ?? '-'}`,
      },
    ],
    sort: [
      {
        name: `${RecruiterOrderByColumn.RecruiterOwnershipDays}_${SortOrder.Asc}`,
        orderByColumn: RecruiterOrderByColumn.RecruiterOwnershipDays,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Ownership days'),
        secondaryText: $t('Low to high'),
        title: $t('Order by ownership days'),
      },
      {
        name: `${RecruiterOrderByColumn.RecruiterOwnershipDays}_${SortOrder.Desc}`,
        orderByColumn: RecruiterOrderByColumn.RecruiterOwnershipDays,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Ownership days'),
        secondaryText: $t('High to low'),
        title: $t('Order by ownership days'),
      },
    ],
  },
  CompanyRecruiterSubmissionLimitColumn: {
    fragment: CompanyRecruiterSubmissionLimitColumnFragmentDoc,
    component: markRaw(CompanyRecruiterSubmissionLimitColumn),
    columnDef: {
      title: $t('Candidate limit'),
      key: 'CompanyRecruiterSubmissionLimitColumn',
      renderUsing: {
        component: markRaw(CompanyRecruiterSubmissionLimitColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Candidate limit',
        extraction: (row: CompanyRecruiterSubmissionLimitColumnFragment) =>
          `${row.maxCandidateSubmissions ?? '-'}`,
      },
    ],
  },
  CompanyRecruiterTypeColumn: {
    fragment: CompanyRecruiterTypeColumnFragmentDoc,
    component: markRaw(CompanyRecruiterTypeColumn),
    columnDef: {
      title: $t('Type'),
      key: 'CompanyRecruiterTypeColumn',
      renderUsing: {
        component: markRaw(CompanyRecruiterTypeColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Type',
        extraction: (row: { recruiterManagesWorkers: boolean }) =>
          row.recruiterManagesWorkers ? $t('Managed') : $t('Referral fee'),
      },
    ],
  },
  CompanyRecruiterTagsColumn: {
    fragment: CompanyRecruiterTagsColumnFragmentDoc,
    component: markRaw(CompanyRecruiterTagsColumn),
    columnDef: {
      title: $t('Tags'),
      key: 'CompanyRecruiterTagsColumn',
      renderUsing: {
        component: markRaw(CompanyRecruiterTagsColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'Tags',
        extraction: (row: CompanyRecruiterTagsColumnFragment) =>
          row.tags.map((t) => t.name).join(', '),
      },
    ],
  },
  CompanyRecruiterEndClientsColumn: {
    fragment: CompanyRecruiterEndClientsColumnFragmentDoc,
    component: markRaw(CompanyRecruiterEndClientsColumn),
    columnDef: {
      title: $t('End clients'),
      key: 'CompanyRecruiterEndClientsColumn',
      orderAble: false,
      renderUsing: {
        component: markRaw(CompanyRecruiterEndClientsColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
    },
    dataDescriber: [
      {
        title: 'End clients',
        extraction: (row: CompanyRecruiterEndClientsColumnFragment) =>
          row.endClients.data.map((link) => link.client.name).join(', '),
      },
    ],
  },
  CompanyRecruiterActionColumn: {
    fragment: CompanyRecruiterActionColumnFragmentDoc,
    component: markRaw(CompanyRecruiterActionColumn),
    columnDef: {
      title: '',
      key: 'CompanyRecruiterActionColumn',
      renderUsing: {
        component: markRaw(CompanyRecruiterActionColumn),
        componentProps: (row: any, rowIndex?: number) => ({ row, rowIndex }),
      },
      ignoreClickForRowSelection: true,
    },
    dataDescriber: [],
  },
}

export default columns
